import * as React from "react";

import { createNotifier, errorNotification } from "@/react/notifications";
import { KEY } from "@/react/components/table/useTableMutations";
import { filterTableMutations } from "@/react/components/table/filterTableMutations";
import { handleMutationResponse as createMutationResponseHandler } from "@/react/services/api/handleMutationResponse";
import { useDeleteQueuesMutation } from "@/react/features/vcc/common/api/queues";

import schema from "./schema";

const NOTIFIER = createNotifier("delete", "queue");

/**
 * Custom hook to handle table mutations for queues.
 *
 * This hook currently only supports the deletion of entries (queues).
 *
 * @param {string} tenantName - The name of the tenant.
 * @param {object} mutations - The object containing rows to delete and mutation handlers.
 * @returns {object} - An object containing the onSave and onCancel functions, isMutationOngoing boolean, and filteredMutations.
 */
const useMutations = (tenantName, mutations) => {
  const { rowsToDelete, setRowsToDelete, setData, resetData } = mutations;
  const [deleteQueues, { isLoading }] = useDeleteQueuesMutation();
  const filteredMutations = React.useMemo(
    () => filterTableMutations(mutations, { delete: true }),
    [mutations]
  );
  const onDeleteResponseHandler = React.useMemo(
    () =>
      createMutationResponseHandler({
        rows: rowsToDelete,
        setRows: setRowsToDelete,
        setData,
        responseMappingKey: schema.fields.id.spec.key,
        idKey: KEY,
        customFallbackKey: schema.fields.name.spec.key,
        notifyContent: NOTIFIER,
        isVoidResponse: true,
      }),
    [rowsToDelete, setRowsToDelete, setData]
  );

  const onSave = React.useCallback(() => {
    if (rowsToDelete.size > 0) {
      const queueIds = Array.from(rowsToDelete.keys());
      deleteQueues({ tenantName, queueIds })
        .unwrap()
        .then(onDeleteResponseHandler)
        .catch((error) => {
          console.error("Failed to delete queues:", error);
          errorNotification(error);
        });
    }
  }, [rowsToDelete, deleteQueues, tenantName, onDeleteResponseHandler]);

  return {
    onSave,
    onCancel: resetData,
    isMutationOngoing: isLoading,
    filteredMutations,
    isDirty: rowsToDelete.size > 0,
  };
};

export default useMutations;
